import { atom } from 'jotai';
import { isEqual } from 'lodash-es';
import type { FolderId, ViewModelId, WorkspaceId } from 'balus-common';
import type { ViewModelEntity } from '@view-model/domain/view-model';
import type { Workspace } from '@workspace/domain/workspace';
import type { Folder } from '@workspace/view-model-folder/domain/Folder';
import type { RootFolderTree } from '@workspace/view-model-folder/domain/FolderTree';
import type { SidebarSearchRecords } from '../SidebarSearchRecords';
import { filterByKeyword } from '../filterByKeyword';
import { sidebarSearchKeywordAtom } from '../sidebarSearchKeywordAtom';

const allSearchWorkspacesAtom = atom<Record<WorkspaceId, Workspace>>({});
export const allSearchFolderTreesAtom = atom<Record<WorkspaceId, RootFolderTree>>({});
const allSearchFoldersAtom = atom<Record<FolderId, Folder>>({});
const allSearchViewModelsAtom = atom<Record<ViewModelId, ViewModelEntity>>({});

export const setInitialAllSearchRecordsAtom = atom(
  null,
  (
    get,
    set,
    payload: {
      workspaces: Record<WorkspaceId, Workspace>;
      folderTrees: Record<WorkspaceId, RootFolderTree>;
      folders: Record<FolderId, Folder>;
      viewModels: Record<ViewModelId, ViewModelEntity>;
    }
  ) => {
    set(allSearchWorkspacesAtom, payload.workspaces);
    set(allSearchFolderTreesAtom, payload.folderTrees);
    set(allSearchFoldersAtom, payload.folders);
    set(allSearchViewModelsAtom, payload.viewModels);
  }
);

export const addWorkspaceInAllSearchAtom = atom(null, (get, set, payload: { workspace: Workspace }) => {
  const currentWorkspaces = get(allSearchWorkspacesAtom);
  if (currentWorkspaces[payload.workspace.id] && currentWorkspaces[payload.workspace.id].isEqual(payload.workspace)) {
    return;
  }
  set(allSearchWorkspacesAtom, {
    ...currentWorkspaces,
    [payload.workspace.id]: payload.workspace,
  });
});

export const addRootFolderTreeInAllSearchAtom = atom(
  null,
  (get, set, payload: { workspaceId: WorkspaceId; rootFolderTree: RootFolderTree }) => {
    set(allSearchFolderTreesAtom, {
      ...get(allSearchFolderTreesAtom),
      [payload.workspaceId]: payload.rootFolderTree,
    });
  }
);

export const setFolderInAllSearchAtom = atom(null, (get, set, payload: { folder: Folder }) => {
  const currentFolders = get(allSearchFoldersAtom);
  if (currentFolders[payload.folder.id] && isEqual(currentFolders[payload.folder.id].dump(), payload.folder.dump())) {
    return;
  }
  set(allSearchFoldersAtom, {
    ...currentFolders,
    [payload.folder.id]: payload.folder,
  });
});

export const removeFolderInAllSearchAtom = atom(null, (get, set, payload: { folderId: FolderId }) => {
  const { [payload.folderId]: removedFolder, ...newFolders } = get(allSearchFoldersAtom);
  set(allSearchFoldersAtom, newFolders);
});

export const setViewModelInAllSearchAtom = atom(null, (get, set, payload: { viewModel: ViewModelEntity }) => {
  const currentViewModels = get(allSearchViewModelsAtom);
  if (
    currentViewModels[payload.viewModel.id] &&
    currentViewModels[payload.viewModel.id].isEqualExcludingUpdatedAt(payload.viewModel)
  ) {
    return;
  }
  set(allSearchViewModelsAtom, {
    ...currentViewModels,
    [payload.viewModel.id]: payload.viewModel,
  });
});

export const removeViewModelInAllSearchAtom = atom(null, (get, set, payload: { viewModelId: ViewModelId }) => {
  const { [payload.viewModelId]: removedViewModel, ...newViewModels } = get(allSearchViewModelsAtom);
  set(allSearchViewModelsAtom, newViewModels);
});

export const removeBulkInAllSearchAtom = atom(null, (get, set, payload: { workspaceId: WorkspaceId }) => {
  const { workspaceId } = payload;
  const currentWorkspaces = get(allSearchWorkspacesAtom);
  const currentFolderTrees = get(allSearchFolderTreesAtom);
  const currentFolders = get(allSearchFoldersAtom);
  const currentViewModels = get(allSearchViewModelsAtom);

  const { [workspaceId]: targetWorkspace, ...newWorkspaces } = currentWorkspaces;
  const { [workspaceId]: targetFolderTree, ...newFolderTrees } = currentFolderTrees;

  if (!targetFolderTree) return;

  const targetFolderIds = targetFolderTree.getAllFolderIds();
  const targetViewModelIds = targetFolderTree.getAllViewModelIds();

  set(allSearchWorkspacesAtom, newWorkspaces);
  set(allSearchFolderTreesAtom, newFolderTrees);
  set(
    allSearchFoldersAtom,
    Object.entries(currentFolders).reduce((res, [folderId, folder]) => {
      if (targetFolderIds.includes(folderId)) {
        return res;
      } else {
        return {
          ...res,
          [folderId]: folder,
        };
      }
    }, {})
  );
  set(
    allSearchViewModelsAtom,
    Object.entries(currentViewModels).reduce((res, [viewModelId, viewModel]) => {
      if (targetViewModelIds.includes(viewModelId)) {
        return res;
      } else {
        return {
          ...res,
          [viewModelId]: viewModel,
        };
      }
    }, {})
  );
});

export const filteredAllSearchRecordsAtom = atom<SidebarSearchRecords>((get) => {
  const workspaces = get(allSearchWorkspacesAtom);
  const folderTrees = get(allSearchFolderTreesAtom);
  const folders = get(allSearchFoldersAtom);
  const viewModels = get(allSearchViewModelsAtom);
  const searchKeyword = get(sidebarSearchKeywordAtom);

  if (!searchKeyword) {
    return {
      workspaces: {},
      folderTrees: {},
      folders: {},
      viewModels: {},
    };
  }

  return filterByKeyword(searchKeyword, { workspaces, folderTrees, folders, viewModels });
});
